import { ApiClient } from './client';
import type { ApiEnvelope, ApiResponse } from './types';
import { unwrapApiResponseData } from './types';
import { translateClient } from '@/lib/i18n/client';
import type {
  CreateSocialPublishCampaignPayload,
  CreateSocialPublishCompositionPayload,
  CreateSocialPublishJobPayload,
  GenerateSocialPublishEditorialDraftsPayload,
  ConnectSocialPlatformResult,
  SocialPublishCampaign,
  SocialPublishCampaignList,
  SocialPublishJob,
  SocialPublishJobList,
  SocialPlatformAccount,
  SocialPlatformAccountList,
  SocialPublishingProviderStatus,
  SocialPublishPlatform,
  ConsumerSocialPublishPlatform,
  SocialPublishPreflightResult,
  ReviewSocialPublishCampaignPayload,
  SubmitSocialPublishCampaignPayload,
  TrackSocialPublishEventPayload,
  TrackSocialPublishEventResult,
  UpdateSocialPublishEditorialDraftPayload,
} from '@/types/social-publishing';

interface Envelope<T> {
  success: boolean;
  data: T;
}

const SOCIAL_OAUTH_START_TIMEOUT_MS = 20_000;
const SOCIAL_ACCOUNTS_READ_TIMEOUT_MS = 15_000;
const SOCIAL_ACCOUNT_MUTATION_TIMEOUT_MS = 20_000;

function socialRequestTimeoutResponse<T>(): ApiResponse<T> {
  return {
    error: {
      code: 'SOCIAL_REQUEST_TIMEOUT',
      message: translateClient('errors', 'codes.network.message'),
      action: 'retry',
      retryable: true,
    },
  };
}

class SocialPublishingApi extends ApiClient {
  private async requestEnvelope<T>(
    endpoint: string,
    options?: RequestInit,
    timeoutMs?: number,
  ): Promise<ApiResponse<T>> {
    const controller = timeoutMs ? new AbortController() : null;
    let timedOut = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const request = this.request<Envelope<T>>(endpoint, {
      ...options,
      ...(controller ? { signal: controller.signal } : {}),
    });
    const boundedRequest = controller
      ? Promise.race([
        request,
        new Promise<ApiResponse<Envelope<T>>>((resolve) => {
          timeoutId = setTimeout(() => {
            timedOut = true;
            controller.abort();
            resolve(socialRequestTimeoutResponse<Envelope<T>>());
          }, timeoutMs);
        }),
      ])
      : request;

    try {
      const response = await boundedRequest;
      if (timedOut) return socialRequestTimeoutResponse<T>();
      return unwrapApiResponseData<T>(response as ApiResponse<ApiEnvelope<T> | T>, {
        code: 'INVALID_RESPONSE',
        message: translateClient('errors', 'common.fallbackMessage'),
      });
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
  }

  async createJob(payload: CreateSocialPublishJobPayload): Promise<ApiResponse<SocialPublishJob>> {
    return this.requestEnvelope('/social-publishing/jobs', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async createCampaign(payload: CreateSocialPublishCampaignPayload): Promise<ApiResponse<SocialPublishCampaign>> {
    return this.requestEnvelope('/social-publishing/campaigns', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listCampaigns(params?: { limit?: number; offset?: number }): Promise<ApiResponse<SocialPublishCampaignList>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.requestEnvelope(`/social-publishing/campaigns${suffix}`);
  }

  async submitCampaign(campaignId: string, payload: SubmitSocialPublishCampaignPayload = {}): Promise<ApiResponse<SocialPublishCampaign>> {
    return this.requestEnvelope(`/social-publishing/campaigns/${campaignId}/submit`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async requestCampaignReview(campaignId: string, payload: ReviewSocialPublishCampaignPayload = {}): Promise<ApiResponse<SocialPublishCampaign>> {
    return this.requestEnvelope(`/social-publishing/campaigns/${campaignId}/review-request`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async approveCampaign(campaignId: string, payload: ReviewSocialPublishCampaignPayload = {}): Promise<ApiResponse<SocialPublishCampaign>> {
    return this.requestEnvelope(`/social-publishing/campaigns/${campaignId}/approve`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async rejectCampaign(campaignId: string, payload: ReviewSocialPublishCampaignPayload = {}): Promise<ApiResponse<SocialPublishCampaign>> {
    return this.requestEnvelope(`/social-publishing/campaigns/${campaignId}/reject`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async generateEditorialDrafts(
    campaignId: string,
    payload: GenerateSocialPublishEditorialDraftsPayload = {},
  ): Promise<ApiResponse<SocialPublishCampaign>> {
    return this.requestEnvelope(`/social-publishing/campaigns/${campaignId}/editorial-drafts/generate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listJobs(params?: { limit?: number; offset?: number }): Promise<ApiResponse<SocialPublishJobList>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.requestEnvelope(`/social-publishing/jobs${suffix}`);
  }

  async updatePost(jobId: string, payload: { post_url?: string | null; status?: string | null }): Promise<ApiResponse<SocialPublishJob>> {
    return this.requestEnvelope(`/social-publishing/jobs/${jobId}/post`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async updateEditorialDraft(
    jobId: string,
    payload: UpdateSocialPublishEditorialDraftPayload,
  ): Promise<ApiResponse<SocialPublishJob>> {
    return this.requestEnvelope(`/social-publishing/jobs/${jobId}/editorial-draft`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async prepareManualPublish(jobId: string): Promise<ApiResponse<SocialPublishJob>> {
    return this.requestEnvelope(`/social-publishing/jobs/${jobId}/prepare-manual-publish`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async trackEvent(payload: TrackSocialPublishEventPayload): Promise<ApiResponse<TrackSocialPublishEventResult>> {
    return this.requestEnvelope('/social-publishing/events', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listAccounts(): Promise<ApiResponse<SocialPlatformAccountList>> {
    return this.requestEnvelope('/social-publishing/accounts', undefined, SOCIAL_ACCOUNTS_READ_TIMEOUT_MS);
  }

  async getProviderStatus(): Promise<ApiResponse<SocialPublishingProviderStatus>> {
    return this.requestEnvelope('/social-publishing/provider-status');
  }

  async connectAccount(platform: SocialPublishPlatform): Promise<ApiResponse<ConnectSocialPlatformResult>> {
    return this.requestEnvelope('/social-publishing/accounts/connect', {
      method: 'POST',
      body: JSON.stringify({ platform }),
    }, SOCIAL_OAUTH_START_TIMEOUT_MS);
  }

  async disconnectAccount(accountId: string): Promise<ApiResponse<SocialPlatformAccount>> {
    return this.requestEnvelope(`/social-publishing/accounts/${encodeURIComponent(accountId)}`, {
      method: 'DELETE',
    }, SOCIAL_ACCOUNT_MUTATION_TIMEOUT_MS);
  }

  async createComposition(payload: CreateSocialPublishCompositionPayload): Promise<ApiResponse<SocialPublishCampaign>> {
    return this.requestEnvelope('/social-publishing/compositions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listCompositions(params?: { limit?: number; offset?: number }): Promise<ApiResponse<SocialPublishCampaignList>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.requestEnvelope(`/social-publishing/compositions${suffix}`);
  }

  async preflightComposition(compositionId: string): Promise<ApiResponse<SocialPublishPreflightResult>> {
    return this.requestEnvelope(`/social-publishing/compositions/${encodeURIComponent(compositionId)}/preflight`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async submitComposition(compositionId: string): Promise<ApiResponse<SocialPublishCampaign>> {
    return this.requestEnvelope(`/social-publishing/compositions/${encodeURIComponent(compositionId)}/submit`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async retryDelivery(compositionId: string, jobId: string): Promise<ApiResponse<SocialPublishCampaign>> {
    return this.requestEnvelope(
      `/social-publishing/compositions/${encodeURIComponent(compositionId)}/deliveries/${encodeURIComponent(jobId)}/retry`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  async connectConsumerAccount(
    platform: ConsumerSocialPublishPlatform,
    returnContext?: Record<string, unknown>,
  ): Promise<ApiResponse<ConnectSocialPlatformResult>> {
    return this.requestEnvelope('/social-publishing/accounts/connect', {
      method: 'POST',
      body: JSON.stringify({
        platform,
        return_origin: typeof window === 'undefined' ? undefined : window.location.origin,
        return_context: returnContext ?? {},
      }),
    }, SOCIAL_OAUTH_START_TIMEOUT_MS);
  }
}

export const socialPublishingApi = new SocialPublishingApi();
